/**
 * web/src/components/speaker/BatchRunDock.tsx
 * Persistent run dock (design: docs/design_handoff_speaker_batch §3). A small
 * fixed card, bottom-right, that appears whenever a batch run exists and the
 * drawer is closed — so "it's still going / it's ready" survives navigation and
 * reload (the run state lives above the route in BatchRunContext). Clicking
 * reopens the drawer onto the run; a finished run gets a dismiss X.
 *
 * Mounted once in AppShell inside <BatchRunProvider>. Reads all state from the
 * context — purely presentational otherwise.
 */
import { useBatchRun } from './BatchRunContext';
import type { BatchRunAction } from '../../api/speakerBatch';
import { Icon } from '../../lib/icons';
import { cn } from '../../lib/cn';

const CONCURRENCY = 2;
const PER_ITEM_SEC: Record<BatchRunAction, number> = {
  cards: 20,
  logistics: 3,
  promotion: 3,
  prep_notes: 3,
  thank_you: 3,
  // Cards-board stage advances (imagegen-heavy finalize; build is lighter).
  card_finalize: 25,
  card_build: 10,
  card_publish: 3,
  regenerate: 2,
};
const VERB: Record<BatchRunAction, string> = {
  cards: 'Generating cards',
  logistics: 'Opening logistics',
  promotion: 'Opening cards',
  prep_notes: 'Opening prep notes',
  thank_you: 'Opening thank-yous',
  card_finalize: 'Finalizing illustrations',
  card_build: 'Building cards',
  card_publish: 'Publishing cards',
  regenerate: 'Regenerating drafts',
};
const NOUN: Record<BatchRunAction, string> = {
  cards: 'cards',
  logistics: 'emails',
  promotion: 'cards',
  prep_notes: 'emails',
  thank_you: 'emails',
  card_finalize: 'illustrations',
  card_build: 'cards',
  card_publish: 'cards',
  regenerate: 'drafts',
};

function fmtEta(sec: number): string {
  if (sec <= 0) return 'finishing';
  if (sec < 60) return `about ${Math.ceil(sec / 10) * 10}s left`;
  return `about ${Math.round(sec / 60)} min left`;
}

export function BatchRunDock() {
  const { run, drawerOpen, reopenRun, dismiss } = useBatchRun();
  if (run == null || drawerOpen) return null;

  const total = run.items.length;
  const done = run.items.filter((i) => i.status === 'done').length;
  const failed = run.items.filter((i) => i.status === 'failed').length;
  const left = total - done - failed;
  const finished = run.state === 'done';
  const eta = Math.ceil(left / CONCURRENCY) * PER_ITEM_SEC[run.action];

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={reopenRun}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && reopenRun()}
      className={cn(
        'fixed bottom-[22px] right-[22px] z-40 w-[300px] cursor-pointer rounded-lg border border-border-strong bg-bg p-3.5 shadow-[0_14px_40px_rgba(0,0,0,0.16)]',
        'transition-colors hover:border-type-speaker',
      )}
    >
      <div className="flex items-center gap-2">
        {finished ? (
          <Icon
            name={failed ? 'alert' : 'check'}
            size={14}
            className={failed ? 'text-waiting' : 'text-completed'}
          />
        ) : (
          <span className="inline-block h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-accent-border border-t-accent" />
        )}
        <span className="flex-1 truncate text-[13px] font-semibold">
          {finished ? `${done} ${NOUN[run.action]} ready` : VERB[run.action]}
        </span>
        <span className="tabular text-[12px] text-text-subtle">
          {done + failed}/{total}
        </span>
        {finished && (
          <button
            type="button"
            aria-label="Dismiss"
            onClick={(e) => {
              e.stopPropagation();
              dismiss();
            }}
            className="rounded p-0.5 text-text-faint hover:bg-bg-muted hover:text-text"
          >
            <Icon name="x" size={13} />
          </button>
        )}
      </div>
      <div className="mt-2 flex h-1.5 overflow-hidden rounded-full bg-bg-muted">
        <span className="bg-type-speaker transition-[width] duration-300" style={{ width: `${(done / total) * 100}%` }} />
        <span className="bg-failed transition-[width] duration-300" style={{ width: `${(failed / total) * 100}%` }} />
      </div>
      <div className="mt-1.5 flex items-center gap-1 text-[11.5px] text-text-subtle">
        {finished ? (
          <>
            Tap to {failed ? `retry ${failed} or ` : ''}start reviewing
            <Icon name="chevron-right" size={11} />
          </>
        ) : (
          <>
            {fmtEta(eta)} · tap to watch
          </>
        )}
      </div>
    </div>
  );
}
